<template>
  <div>
    <TransitionRoot as="template" :show="openNewPropertyDrawer">
      <Dialog as="div" class="relative z-40" @close="openNewPropertyDrawer = false">
        <div class="fixed inset-0"/>
        <div class="fixed inset-0 overflow-hidden pt-60">
          <div class="absolute inset-0 overflow-hidden pt-60">
            <div class="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10 sm:pl-16">
              <TransitionChild as="template" enter="transform transition ease-in-out duration-500 sm:duration-700" enter-from="translate-x-full" enter-to="translate-x-0" leave="transform transition ease-in-out duration-500 sm:duration-700" leave-from="translate-x-0" leave-to="translate-x-full">
                <DialogPanel class="pointer-events-auto w-screen max-w-2xl">
                  <div class="flex h-full flex-col overflow-y-auto bg-white py-6 shadow-xl">
                    <div class="px-4 sm:px-6">
                      <div class="flex items-start justify-between">
                        <DialogTitle class="text-base font-semibold text-gray-900">{{ $gettext('Ajouter une propriété') }}</DialogTitle>
                        <div class="ml-3 flex h-7 items-center">
                          <button type="button" class="relative rounded-md bg-white text-gray-400 hover:text-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2" @click="openNewPropertyDrawer = false">
                            <span class="absolute -inset-2.5" />
                            <span class="sr-only">Close panel</span>
                            <XMarkIcon class="size-6" aria-hidden="true" />
                          </button>
                        </div>
                      </div>
                    </div>
                    <div class="relative mt-6 flex-1 px-4 sm:px-6">
                      <PropertyForm :property="property" @submit="onSubmit" @cancel="onCancel" />
                    </div>
                  </div>
                </DialogPanel>
              </TransitionChild>
            </div>
          </div>
        </div>
      </Dialog>
    </TransitionRoot>
  </div>
  <Combobox as="div" class="w-full sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-2" v-model="currentProperty" @update:modelValue="query = ''" :nullable="true">
    <div>
      <ComboboxLabel :for="fieldPath" class="block text-sm font-medium text-gray-900 sm:mt-2">
        {{ fieldLabel }}
      </ComboboxLabel>
    </div>
    <div class="sm:col-span-2">
      <div class="flex items-start gap-2">
        <div class="w-80">
          <div class="relative w-full">
            <ComboboxInput
              :id="fieldPath"
              :name="fieldPath"
              class="w-full block rounded-md bg-white py-1.5 pl-3 pr-10 text-base text-gray-900 outline outline-1 outline-gray-300 placeholder:text-gray-400 focus:outline-indigo-600 sm:text-sm"
              @change="query = $event.target.value"
              @blur="query = ''"
              :display-value="(object) => object?.relationships?.propertyType?.data?.attributes?.name ? `${object.relationships.propertyType.data.attributes.name} - ${object.attributes.fullAddress}` : object?.attributes.fullAddress"
            />
            <ComboboxButton class="absolute inset-y-0 right-0 flex items-center pr-2">
              <ChevronUpDownIcon class="size-4 text-gray-400" aria-hidden="true" />
            </ComboboxButton>
          </div>
          <ComboboxOptions
            class="absolute z-50 max-h-56 w-80 mt-1 overflow-auto rounded-md bg-white text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm">
            <li
              @mousedown.prevent.stop
              @click.prevent.stop="toggleNewPropertyDrawer()"
              class="relative cursor-pointer select-none py-2 pl-3 pr-9 text-gray-900 hover:bg-indigo-600 hover:text-white"
            >
              <div class="flex items-center">
                <div class="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                  <PlusCircleIcon class="h-4 w-4 text-gray-700"></PlusCircleIcon>
                </div>
                <span class="ml-3 truncate font-medium">
                  {{ $gettext("Nouvelle propriété") }}
                </span>
              </div>
            </li>
            <span v-if="filteredProperties.length > 0">
              <ComboboxOption
                v-for="filteredProperty in filteredProperties"
                :key="filteredProperty.id"
                :value="filteredProperty"
                as="template"
                v-slot="{ selected }"
              >
              <li
                @mouseenter="hoveredPropertyId = filteredProperty.id"
                @mouseleave="hoveredPropertyId = null"
                :class="['relative cursor-default select-none py-2 pl-3 pr-9', hoveredPropertyId === filteredProperty.id ? 'bg-indigo-600 text-white' : 'text-gray-900']">
                <div class="flex items-start space-x-3">
                  <div class="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0">
                    <MapPinIcon class="h-4 w-4 text-gray-700"></MapPinIcon>
                  </div>
                  <div class="flex flex-col">
                    <span :class="[selected && 'font-semibold', hoveredPropertyId === filteredProperty.id ? 'text-white' : 'text-gray-700']" class="text-xs">
                      {{ filteredProperty.relationships?.propertyType?.data?.attributes?.name }}
                    </span>
                    <span :class="[selected && 'font-semibold']" class="break-words">
                      {{ filteredProperty.attributes.fullAddress }}
                    </span>
                  </div>
                </div>
              </li>

              </ComboboxOption>
            </span>
          </ComboboxOptions>
        </div>
      </div>
      <FormFieldError :field-path="fieldPath" :errors="errors" />
    </div>
  </Combobox>
</template>

<script setup>
import { computed, ref, watch, onMounted } from "vue";
import { ChevronUpDownIcon } from "@heroicons/vue/20/solid";
import { PlusCircleIcon, MapPinIcon, XMarkIcon } from "@heroicons/vue/24/outline";
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from "@headlessui/vue";

import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxLabel,
  ComboboxOption,
  ComboboxOptions,
} from "@headlessui/vue";
import { usePropertyStore } from "@/stores/property.js";
import FormFieldError from "./FormFieldError.vue";
import PropertyForm from "@/components/properties/PropertyForm.vue";

const props = defineProps(["modelValue", "fieldPath", "fieldAttrs", "fieldLabel", "errors"]);
const emit = defineEmits(["update:modelValue"]);

const openNewPropertyDrawer = ref(false);
const propertyStore = usePropertyStore();
const query = ref("");
const currentProperty = ref(null);
const property = propertyStore.empty;
const hoveredPropertyId = ref(null);

const filteredProperties = computed(() => {
  if (query.value === "") {
    return propertyStore.all.filter((property) => {
      const isAlreadySelected = currentProperty.value !== null && currentProperty.value.id === property.id;
      return !isAlreadySelected;
    });
  } else {
    return propertyStore.all.filter((property) => {
      const matches = property.attributes.fullAddress.toLowerCase().includes(query.value.toLowerCase());
      const isAlreadySelected = currentProperty.value !== null && currentProperty.value.id === property.id;
      return matches && !isAlreadySelected;
    });
  }
});

watch(currentProperty, (property) => {
  if (property) {
    selectProperty(property);
  }
});

watch(() => props.modelValue, (newModelValue, oldModelValue) => {
  if (newModelValue && oldModelValue === undefined) {
    selectProperty(currentProperty);
  }
});

const selectProperty = (property) => {
  currentProperty.value = property;
  emit("update:modelValue", currentProperty.value);
};

const toggleNewPropertyDrawer = () => {
  openNewPropertyDrawer.value = !openNewPropertyDrawer.value;
};

const onSubmit = (payload, actions) => {
  propertyStore.save(payload, (resource) => {
    toggleNewPropertyDrawer();
    selectProperty(resource);
  }, (errors) => {
    actions.setErrors(errors.value);
  });
};

const onCancel = () => {
  toggleNewPropertyDrawer();
};

onMounted(async () => {
  if (props.modelValue !== undefined && props.modelValue.id) {
    selectProperty(props.modelValue);
  }
});

</script>
